import React, { useState } from "react";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ReportOutlinedIcon from "@mui/icons-material/ReportOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";

import { Dialog } from "@/components/main/utils/Dialog";
import { DialogActions } from "@/components/main/utils/DialogActions";
import { Button } from "components/common/Button";
import { Spinner } from "components/common/Spinner";
import { DeleteDialogSection } from "components/main/utils/models/deletion/DeleteDialogSection";
import { OwnershipTransferModal } from "@/components/canvas/detailsSide/utils/OwnershipTransferModal";
import { useDeleteAccount, type TableWithModel } from "./useDeleteAccount";
import { useAuth } from "contexts/AuthProvider";
import { axiosInstance } from "contexts/axiosApiInstance";
import { showNotificationGlobal } from "stores/useNotificationStore";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { TooltipButton } from "components/common/TooltipButton";
import { OrgMark } from "@/components/main/organizations/OrgMark";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import { CriticalTableRow } from "./CriticalTableRow";
import { plural } from "components/common/setUtils";

interface DeleteAccountDialogProps {
  open: boolean;
  onClose: () => void;
}

/// The 409 body from `/delete_account` when the caller is the last owner of
/// an org. Refusing here is what keeps an org from being stranded with no
/// owner and no in-product way back.
interface SoleOwnerRefusal {
  reason: string;
  message: string;
  organizations: { org_id: string; name: string }[];
}

/// D55: the press is the org press on the account's clock — the personal org,
/// every space and model in it go to the Trash for 48 hours, and cancelling
/// brings all of it back. Anyone who imported a table has 10 days after the
/// purge to copy it.
/// The one pre-emptive action is moving a table into a model in a space you
/// manage — the manager set outlives you, so the table survives in full.
export const DeleteAccountDialog: React.FC<DeleteAccountDialogProps> = ({
  open,
  onClose,
}) => {
  const { setAccountStatus } = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [movingTable, setMovingTable] = useState<TableWithModel | null>(null);
  const knownOrgs = useOrgContextStore((s) => s.orgs);
  const [soleOwnerRefusal, setSoleOwnerRefusal] =
    useState<SoleOwnerRefusal | null>(null);

  const {
    loading,
    error,
    analysis,
    allTables,
    criticalTables,
    totalCriticalTables,
    refresh,
    handleCancel: hookCancel,
  } = useDeleteAccount(open, onClose);

  const handleCancel = () => {
    setShowConfirmation(false);
    hookCancel();
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      await axiosInstance.post("/action/delete_account", {});
      setAccountStatus("scheduledfordeletion");
      showNotificationGlobal(
        "Account scheduled for deletion. You have 48 hours to cancel.",
        "success",
      );
      onClose();
    } catch (err) {
      console.error("Failed to schedule account deletion:", err);
      // The sole-owner refusal is actionable, so it must not collapse into
      // the generic failure toast — name the orgs and what to do about them.
      const data = (err as { response?: { data?: SoleOwnerRefusal } })?.response
        ?.data;
      if (data?.reason === "sole_org_owner") {
        setSoleOwnerRefusal(data);
      } else {
        showNotificationGlobal("Failed to schedule account deletion", "error");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const orgMark = (o: { org_id: string; name: string }) => {
    const known = knownOrgs.find((k) => k.org_id === o.org_id);
    return { org_id: o.org_id, name: o.name, has_image: !!known?.has_image };
  };

  if (soleOwnerRefusal) {
    return (
      <Dialog
        open={open}
        title="Hand over your organizations first"
        onClose={() => {
          setSoleOwnerRefusal(null);
          handleCancel();
        }}
        className="max-w-140"
      >
        <span className="text-fg-regular">
          You are the only owner of{" "}
          {soleOwnerRefusal.organizations.length === 1
            ? "this organization"
            : "these organizations"}
          . Deleting your account would leave{" "}
          {soleOwnerRefusal.organizations.length === 1 ? "it" : "them"} with no
          owner and no way to recover one.
        </span>
        <div className="flex flex-col gap-px pt-2">
          {soleOwnerRefusal.organizations.map((o) => (
            <span
              key={o.org_id}
              className="group flex items-center gap-2 rounded-app px-2 item-height hover:bg-list-hover"
            >
              <OrgMark org={orgMark(o)} size="sm" />
              <span className="min-w-0 flex-1 truncate text-fg-regular">
                {o.name}
              </span>
              {/* A new tab, not a navigate: this dialog is mid-flow and the
                  fix happens over there. */}
              <span className="flex shrink-0 items-center opacity-0 transition-opacity group-hover:opacity-100 has-[:focus-visible]:opacity-100">
                <TooltipButton
                  title="Open settings in a new tab"
                  aria-label={`Open settings for ${o.name} in a new tab`}
                  variant="contextual"
                  size="xs"
                  onClick={() =>
                    window.open(
                      `/organizations/${o.org_id}`,
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                >
                  <SettingsOutlinedIcon fontSize="inherit" />
                </TooltipButton>
              </span>
            </span>
          ))}
        </div>
        <span className="text-fg-muted-regular-small">
          Add a second owner in the organization&apos;s settings, hand it to
          someone else, or delete it. Then come back here.
        </span>
        <DialogActions
          actions={[
            {
              label: "Close",
              variant: "secondary",
              onClick: () => {
                setSoleOwnerRefusal(null);
                handleCancel();
              },
            },
          ]}
        />
      </Dialog>
    );
  }

  if (loading || !analysis) {
    return (
      <Dialog
        open={open}
        title="Delete Account"
        onClose={handleCancel}
        className="max-w-140"
      >
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      </Dialog>
    );
  }

  if (showConfirmation || allTables.length === 0) {
    return (
      <Dialog
        open={open}
        title="Delete Account"
        onClose={handleCancel}
        className="max-w-140"
      >
        <div className="flex flex-col gap-4">
          <div className="flex gap-2 items-center">
            <WarningAmberOutlinedIcon
              sx={{ fontSize: 14, color: "var(--color-warning)" }}
            />
            <span className="text-fg-regular">
              This action will permanently delete your account after 48 hours.
            </span>
          </div>

          <ul className="flex flex-col gap-1.5 text-fg-muted-regular">
            {[
              "Your organization, every space in it and every model you own go to the Trash for 48 hours — you can still sign in and cancel, and everything comes back",
              allTables.length > 0 &&
                `After that, ${plural(allTables.length, "table")} and every model you own are deleted for good`,
              totalCriticalTables > 0 &&
                `${plural(totalCriticalTables, "table")} imported by other people stay copyable for 10 days after that, then go too`,
              "Models in organization spaces you manage are NOT affected — they belong to the space",
              "If you're the only admin of a team, a member will be automatically promoted to admin",
              "Teams you own with no other members will be permanently deleted",
            ]
              .filter(Boolean)
              .map((text, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-fg-muted">&#8226;</span>
                  <span>{text}</span>
                </li>
              ))}
          </ul>

          <div className="flex justify-end gap-2">
            {showConfirmation && (
              <Button
                variant="secondary"
                onClick={() => setShowConfirmation(false)}
              >
                Back
              </Button>
            )}
            <Button
              onClick={handleDeleteAccount}
              loading={isDeleting}
              variant="destructive"
            >
              {isDeleting ? "Deleting..." : "Delete Account"}
            </Button>
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog
        open={open && !movingTable}
        title="Delete Account"
        onClose={handleCancel}
        className="w-175"
        actions={
          <div className="flex items-center justify-end w-full">
            <Button variant="primary" onClick={() => setShowConfirmation(true)}>
              Continue
            </Button>
          </div>
        }
      >
        <div className="flex flex-col max-h-[70vh]">
          <div className="mb-4 shrink-0 flex flex-col gap-3">
            <div className="text-fg-medium">
              {plural(allTables.length, "table")} will be deleted with your
              account.
            </div>
            {totalCriticalTables > 0 && (
              <div className="bg-accent-15 rounded-app p-3 flex gap-2 items-start">
                <ContentCopyOutlinedIcon className="icon-sm text-fg-muted-regular shrink-0 mt-0.5" />
                <span className="text-fg-regular">
                  {plural(totalCriticalTables, "table")} imported by other
                  people. They can copy it into their own models for{" "}
                  <strong>10 days</strong> after your account is deleted. To
                  keep a table alive instead, move it into a model in a space
                  someone else manages too.
                </span>
              </div>
            )}
            {error && (
              <div className="p-3 bg-error/10 border border-error/30 rounded-app text-error text-sm">
                {error}
              </div>
            )}
          </div>

          <DeleteDialogSection
            icon={<ReportOutlinedIcon className="icon-sm text-error" />}
            title={`Tables (${allTables.length})`}
            className="min-h-0 p-4 overflow-y-auto"
          >
            {allTables.map((table) => (
              <CriticalTableRow
                key={table.table_id}
                table={table}
                importedByOthers={criticalTables.some(
                  (t) => t.table_id === table.table_id,
                )}
                onMove={() => setMovingTable(table)}
              />
            ))}
          </DeleteDialogSection>
        </div>
      </Dialog>

      {movingTable && (
        <OwnershipTransferModal
          currentModelId={movingTable.model_id}
          tableId={movingTable.table_id}
          tableName={movingTable.table_name}
          // A connector-driven table cannot enter the catalog; the picker's own
          // per-destination verdicts cover every other refusal.
          canTransferTable={false}
          currentModelIsCatalog={false}
          open={true}
          onOpenChange={(next) => {
            if (!next) {
              setMovingTable(null);
              refresh();
            }
          }}
        />
      )}
    </>
  );
};
